import React from "react";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  Calendar,
  AlertTriangle,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface SystemHealthSummaryProps {
  data: any;
}

export const SystemHealthSummary: React.FC<SystemHealthSummaryProps> = ({
  data,
}) => {
  const healthData = {
    systemHealthIndex: {
      value: data?.overview?.systemHealthIndex?.value || 0,
      change: data?.overview?.systemHealthIndex?.trend || 0,
    },
    systemAvailability: {
      value: data?.overview?.facilityAvailability?.value || 0,
      change: data?.overview?.facilityAvailability?.trend || 0,
    },
    outOfServiceAssets: {
      count: data?.overview?.outOfServiceAssets?.value || 0,
      percentage: data?.overview?.outOfServiceAssets?.percentage || 0,
    },
    avgRUL: {
      value: data?.overview?.avgRemainingLife?.value || 0,
      unit: data?.overview?.avgRemainingLife?.unit || "",
    },
  };

  const statsData = [
    {
      title: "System Condition Index",
      count: healthData.systemHealthIndex.value || 0,
      icon: <Activity className="h-8 w-8 text-blue-500" />,
      tooltip:
        "A composite score reflecting the overall health and operational status of this specific system, based on aggregated asset health, performance, and risk metrics within the system.",
    },
    {
      title: "System Availability",
      count: `${healthData.systemAvailability.value || 0}%`,
      icon: <Shield className="h-8 w-8 text-green-500" />,
      tooltip:
        "The percentage of time the system is operational and available for its intended use, indicating uptime and readiness of the system.",
    },
    {
      title: "Out of Service Assets",
      count: `${healthData.outOfServiceAssets.count || 0} (${healthData.outOfServiceAssets.percentage || 0}%)`,
      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
      tooltip:
        "The number and proportion of assets within this system that are currently non-operational due to maintenance, repair, or other issues.",
    },
    {
      title: "Avg RUL System Assets",
      count: `${healthData.avgRUL.value || 0} ${healthData.avgRUL.unit || ""}`,
      icon: <Calendar className="h-8 w-8 text-purple-500" />,
      tooltip:
        "The average estimated remaining operational lifespan of all assets comprising this system, crucial for long-term capital planning for the system.",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statsData.map((stat, index) => (
        <QuickStatsCard
          key={index}
          title={stat.title}
          count={stat.count}
          icon={stat.icon}
          tooltip={stat.tooltip}
        />
      ))}
    </div>
  );
};
